import * as Dialog from '@radix-ui/react-dialog';
import {
  type QbBulkLinkInput,
  QbBulkLinkResponse,
  type QbLinkCandidate,
  type QbLinkCandidatesResponse,
  QbLinkCandidatesResponse as QbLinkCandidatesResponseSchema,
} from '@uit/shared/schemas/quickbooks';
import { type ReactNode, useMemo, useState } from 'react';
import { ApiError, apiMutation, useApi } from '../../lib/api.js';
import s from './LinkReviewDialog.module.scss';

export interface LinkScope {
  vendorId: string;
  vendorName: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set for the per-row "Link…": review just this vendor and its services. */
  scope?: LinkScope | null;
  onApplied: (result: QbBulkLinkResponse) => void;
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const MATCH_LABEL: Record<QbLinkCandidate['match'], string> = {
  exact: 'Exact',
  suggested: 'Suggested',
  none: 'No match',
};

/**
 * Bulk "Link to QuickBooks" review (T-205). The server dry-runs name matching
 * against QuickBooks; nothing is written until Apply, and apply only records
 * which QB vendor/service each portal record is — it never creates or edits
 * anything in QuickBooks. Exact matches come pre-checked; suggestions (same
 * name modulo case/punctuation/LLC-style suffix) are opt-in.
 */
export function LinkReviewDialog({ open, onOpenChange, scope = null, onApplied }: Props): ReactNode {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>
            <span className={s.qbmark}>qb</span>
            {scope ? `Link “${scope.vendorName}” to QuickBooks` : 'Link to QuickBooks — review matches'}
          </Dialog.Title>
          <Dialog.Description className={s.description}>
            Matched against your QuickBooks company by name.{' '}
            <strong>Nothing is created or changed in QuickBooks</strong> — this only records which
            QB vendor / service each portal record is.
          </Dialog.Description>
          <ReviewBody
            scope={scope}
            onCancel={() => onOpenChange(false)}
            onApplied={(result) => {
              onApplied(result);
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Mounted fresh each time the dialog opens, so every open re-runs the dry run.
function ReviewBody({
  scope,
  onCancel,
  onApplied,
}: {
  scope: LinkScope | null;
  onCancel: () => void;
  onApplied: (result: QbBulkLinkResponse) => void;
}): ReactNode {
  const url = scope
    ? `/api/quickbooks/link-candidates?vendor_id=${encodeURIComponent(scope.vendorId)}`
    : '/api/quickbooks/link-candidates';
  const { data, error } = useApi<QbLinkCandidatesResponse>(url, QbLinkCandidatesResponseSchema);
  // null = untouched: the selection is just the exact matches, derived (not synced by an effect,
  // which would flash them unchecked on first paint).
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const all = useMemo(() => (data ? [...data.vendors, ...data.services] : []), [data]);
  const exactIds = useMemo(() => all.filter((c) => c.match === 'exact').map((c) => c.id), [all]);

  const selected = useMemo(() => picked ?? new Set(exactIds), [picked, exactIds]);

  function toggle(id: string, on: boolean): void {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setPicked(next);
  }

  async function apply(): Promise<void> {
    if (!data) return;
    setBusy(true);
    setApplyError(null);
    const body: QbBulkLinkInput = {
      vendors: data.vendors
        .filter((c) => selected.has(c.id) && c.qb_id)
        .map((c) => ({ vendor_id: c.id, qb_vendor_id: c.qb_id! })),
      services: data.services
        .filter((c) => selected.has(c.id) && c.qb_id)
        .map((c) => ({ engagement_id: c.id, qb_service_id: c.qb_id! })),
    };
    try {
      onApplied(
        await apiMutation<QbBulkLinkInput, QbBulkLinkResponse>('/api/quickbooks/link-bulk', body, {
          schema: QbBulkLinkResponse,
        }),
      );
    } catch {
      setApplyError('Could not save the links. Nothing was changed — try again.');
      setBusy(false);
    }
  }

  if (error) {
    return (
      <>
        <div className={s.error} role="alert">
          {error instanceof ApiError && error.status === 409
            ? 'QuickBooks is not connected. Connect it in Settings, then try again.'
            : 'Could not search QuickBooks. Try again.'}
        </div>
        <div className={s.foot}>
          <span />
          <button type="button" className={s.btn} onClick={onCancel}>
            Close
          </button>
        </div>
      </>
    );
  }
  if (!data) {
    return <div className={s.searching}>Searching QuickBooks…</div>;
  }

  const count = (m: QbLinkCandidate['match']): number => all.filter((c) => c.match === m).length;

  return (
    <>
      {all.length === 0 ? (
        <p className={s.searching}>Nothing to link — everything is already linked.</p>
      ) : (
        <>
          <div className={s.summary}>
            <span className={`${s.pill} ${s.exact}`}>{count('exact')} exact</span>
            <span className={`${s.pill} ${s.sugg}`}>{count('suggested')} suggested</span>
            <span className={`${s.pill} ${s.none}`}>{count('none')} no match</span>
            <span className={s.muted}>
              {plural(data.vendors.length, 'vendor')} · {plural(data.services.length, 'service')}
            </span>
            <button
              type="button"
              className={s.linkBtn}
              onClick={() => setPicked(null)}
            >
              Select all exact
            </button>
          </div>
          <div className={s.scroll}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th scope="col">
                    <span className={s.srOnly}>Select</span>
                  </th>
                  <th scope="col">Portal record</th>
                  <th scope="col">QuickBooks match</th>
                  <th scope="col">Result</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.length > 0 && (
                  <>
                    <tr className={s.group}>
                      <td colSpan={4}>{data.vendors.length === 1 ? 'Vendor' : 'Vendors'}</td>
                    </tr>
                    {data.vendors.map((c) => (
                      <Row key={c.id} c={c} checked={selected.has(c.id)} onToggle={toggle} />
                    ))}
                  </>
                )}
                {data.services.length > 0 && (
                  <>
                    <tr className={s.group}>
                      <td colSpan={4}>Services (engagement product/service)</td>
                    </tr>
                    {data.services.map((c) => (
                      <Row key={c.id} c={c} checked={selected.has(c.id)} onToggle={toggle} />
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {applyError && (
        <div className={s.error} role="alert">
          {applyError}
        </div>
      )}
      <div className={s.foot}>
        <span className={s.audit}>
          Exact matches are pre-selected. Suggested matches differ slightly in spelling — tick them
          only if they&apos;re the same business. Each link is written to the audit log.
        </span>
        <div className={s.footBtns}>
          <button type="button" className={s.btn} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={s.btnPrimary}
            disabled={selected.size === 0 || busy}
            onClick={() => void apply()}
          >
            {`Link ${selected.size} selected`}
          </button>
        </div>
      </div>
    </>
  );
}

function Row({
  c,
  checked,
  onToggle,
}: {
  c: QbLinkCandidate;
  checked: boolean;
  onToggle: (id: string, on: boolean) => void;
}): ReactNode {
  const pillClass = c.match === 'exact' ? s.exact : c.match === 'suggested' ? s.sugg : s.none;
  return (
    <tr>
      <td className={s.checkCell}>
        <input
          type="checkbox"
          aria-label={`Link ${c.name}`}
          checked={checked}
          disabled={c.match === 'none'}
          onChange={(e) => onToggle(c.id, e.target.checked)}
        />
      </td>
      <td>
        {c.name}
        {c.vendor_name && <div className={s.sub}>{c.vendor_name}</div>}
      </td>
      <td>
        {c.match === 'none' ? (
          <span className={s.muted}>
            {c.note ?? 'Not found in QuickBooks'}
            {' · '}
            <a className={s.linkBtn} href={`/vendors/${c.vendor_id}`}>
              Pick manually…
            </a>
          </span>
        ) : (
          <span className={s.qbName}>
            {c.qb_name}
            <span className={s.sub}>
              QB id {c.qb_id}
              {c.note ? ` · ${c.note}` : ''}
            </span>
          </span>
        )}
      </td>
      <td>
        <span className={`${s.pill} ${pillClass}`}>{MATCH_LABEL[c.match]}</span>
      </td>
    </tr>
  );
}
